interface KeyValueFieldConfig {
    field: string;
    key: string;
    extractor: (state: any) => any;
    formatter?: (value: any) => string | null;
}

interface MultiColumnRowConfig {
    title: string;
    key: string;
    formatter?: (value: any) => string | null;
}

interface ChronologyRemarksConfig {
    title: string;
    key: string;
    extractor: (state: any) => any;
}

type TabConfig =
    | {
        tabName: string;
        layout: 'key-value';
        fields: KeyValueFieldConfig[];
    }
    | {
        tabName: string;
        layout: 'multi-column';
        recordsExtractor: (state: any) => any[];
        rows: MultiColumnRowConfig[];
        transpose?: boolean;
    }
    | {
        tabName: string;
        layout: 'evidence';
    }
    | {
        tabName: string;
        layout: 'recommendations';
        recordsExtractor: (state: any) => any;
        rows: any;
        transpose?: boolean;
    }
    | {
        tabName: string;
        layout: 'chronology';
        remarks: ChronologyRemarksConfig[];
    };


export class ReportDataService {

    public static flattenToCommaString(inputValues: any): string | null {
        if (!inputValues) return null;
        if (Array.isArray(inputValues)) return inputValues.join(", ");
        if (typeof inputValues === 'string') return inputValues;
        return null;
    }


    public static concatenateFields(values: Record<string, any>, separator: string = ' / '): string | null {
        if (!values || typeof values !== 'object') return null;
        const validValues = Object.values(values).filter(Boolean);
        return validValues.length > 0 ? validValues.join(separator) : null;
    }


    public static formatDate(dateValue: any): string | null {
        if (!dateValue) return null;
        try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return null;
            return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
        } catch (error) {
            return null;
        }
    }


    public static formatCurrency(num: any): string | null {
        const number = Number(num);
        if (isNaN(number)) return null;
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(number);
    }


    public static processAllTabs(state: any): any[] {
        return ALL_TABS_CONFIG.map(tabConfig => {
            let processedData;

            switch (tabConfig.layout) {
                case 'key-value':
                    processedData = this._processKeyValueTab(tabConfig, state);
                    break;
                
                case 'multi-column':
                    processedData = this._processMultiColumnTab(tabConfig, state);
                    break;
                
                case 'recommendations':
                    processedData = this._processRecommendationsTab(tabConfig, state);
                    break;

                case 'evidence':
                    processedData = null;
                    break;

                case 'chronology':
                    processedData = this._processChronologyTab(tabConfig, state || {});
                    break;

                default:
                    console.error(`Unknown tab layout "${(tabConfig as any).layout}".`);
                    processedData = { fields: [] }; 
            }

            return {
                tabName: tabConfig.tabName,
                layout: tabConfig.layout,
                data: processedData,
            };
        });
    }

    private static _processRecommendationsTab (tabConfig: TabConfig & { layout: 'recommendations' }, state: any) {
        const UNAVAILABLE_TEXT = 'Data Not Available';
        try {
            const  { persons_involved, policy_improvements } = tabConfig.recordsExtractor(state);
            let personInvolvedConfig: any = {}, policyImprovementConfig: any = {};
            const [partyRows, policyRows] = tabConfig.rows;

            // Person Involved
            if (!persons_involved || !Array.isArray(persons_involved) || persons_involved.length === 0) {
                personInvolvedConfig = {
                    title: partyRows?.title,
                    headers: [],
                    rows: [],
                    transpose: partyRows?.transpose || false
                };
            }
            else {
                const personInvolvedHeaders = persons_involved.map((record: any) => {
                    return record?.name || record?.loan_reference_id || 'VALUE';
                });
                personInvolvedConfig.headers = ['Field', ...personInvolvedHeaders];

                const personInvolvedProcessedRows = partyRows?.rows?.map((rowConfig: any) => {
                    const values = persons_involved.map((record: any) => {
                        let finalValue: string | null = UNAVAILABLE_TEXT;
                        try {
                            const rawValue = record?.[rowConfig.key];
                            if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                                finalValue = rowConfig.formatter
                                    ? rowConfig.formatter(rawValue)
                                    : String(rawValue);
                            }
                        } catch (e) {
                            console.error(`Error processing row "${rowConfig.title}" for record in tab "${tabConfig.tabName}":`, e);
                        }
                        return finalValue;
                    });
                    return { title: rowConfig.title, values };
                });
                personInvolvedConfig.rows = personInvolvedProcessedRows;
                personInvolvedConfig.title = partyRows.title;
                personInvolvedConfig.transpose = partyRows?.transpose || false;
            }

            // Policy Improvement
            if (!policy_improvements || !Array.isArray(policy_improvements) || policy_improvements.length === 0) {
                policyImprovementConfig = {
                    title: policyRows?.title,
                    headers: [],
                    rows: [],
                    transpose: policyRows?.transpose || false
                };
            }
            else {
                const policyImprovementHeaders = policy_improvements.map((record: any) => {
                    return record?.name || record?.loan_reference_id || 'VALUE';
                });
                policyImprovementConfig.headers = ['Field', ...policyImprovementHeaders];

                const policyImprovementProcessedRows = policyRows?.rows?.map((rowConfig: any) => {
                    const values = policy_improvements.map((record: any) => {
                        let finalValue: string | null = UNAVAILABLE_TEXT;
                        try {
                            const rawValue = record?.[rowConfig.key];
                            if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                                finalValue = rowConfig.formatter
                                    ? rowConfig.formatter(rawValue)
                                    : String(rawValue);
                            }
                        } catch (e) {
                            console.error(`Error processing row "${rowConfig.title}" for record in tab "${tabConfig.tabName}":`, e);
                        }
                        return finalValue;
                    });
                    return { title: rowConfig.title, values };
                });
                policyImprovementConfig.rows = policyImprovementProcessedRows;
                policyImprovementConfig.title = policyRows.title;
                policyImprovementConfig.transpose = policyRows.transpose || false;
            }

            return {
                policyImprovementConfig,
                personInvolvedConfig,
                transpose: tabConfig.transpose || false 
            };
        } catch(e) {
            console.error(`A critical error occurred while processing multi-column tab "${tabConfig.tabName}".`, e);
            return { headers: [], rows: [], transpose: false };
        }
    }

    private static _processKeyValueTab(tabConfig: TabConfig & { layout: 'key-value' }, state: any) {
        const UNAVAILABLE_TEXT = 'Data Not Available';
        const processedFields = tabConfig.fields.map(fieldConfig => {
            let finalValue: string | null = UNAVAILABLE_TEXT;
            try {
                const rawValue = fieldConfig.extractor(state);
                console.log(`[DEBUG] Processing field: "${fieldConfig.field}". Extracted raw value:`, rawValue);

                if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                     finalValue = fieldConfig.formatter
                        ? fieldConfig.formatter(rawValue)
                        : String(rawValue);
                }
            } catch (e) {
                console.error(`Error processing field "${fieldConfig.field}" in tab "${tabConfig.tabName}":`, e);
            }
            return { field: fieldConfig.field, key: fieldConfig.key, value: finalValue };
        });
        return { fields: processedFields };
    }


    private static _processMultiColumnTab(tabConfig: TabConfig & { layout: 'multi-column' }, state: any) {
        const UNAVAILABLE_TEXT = 'Data Not Available';
        try {
            const records = tabConfig.recordsExtractor(state);
            if (!records || !Array.isArray(records) || records.length === 0) {
                return { headers: [], rows: [], transpose: tabConfig.transpose || false };
            }

            const columnHeaders = records.map((record: any) => {
                return record?.name || record?.loan_reference_id || 'VALUE';
            });
            const headers = ['Field', ...columnHeaders];
            
            const processedRows = tabConfig.rows.map(rowConfig => {
                const values = records.map((record: any) => {
                    let finalValue: string | null = UNAVAILABLE_TEXT;
                    try {
                        const rawValue = record?.[rowConfig.key];
                        if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                            finalValue = rowConfig.formatter
                                ? rowConfig.formatter(rawValue)
                                : String(rawValue);
                        }
                    } catch(e) {
                        console.error(`Error processing row "${rowConfig.title}" for record in tab "${tabConfig.tabName}":`, e);
                    }
                    return finalValue;
                });
                return { title: rowConfig.title, values };
            });

            return { 
                headers, 
                rows: processedRows, 
                transpose: tabConfig.transpose || false 
            };
        } catch(e) {
            console.error(`A critical error occurred while processing multi-column tab "${tabConfig.tabName}".`, e);
            return { headers: [], rows: [], transpose: false };
        }
    }


    private static _processChronologyTab(tabConfig: TabConfig & { layout: 'chronology' }, state: any) {
        const UNAVAILABLE_TEXT = 'Data Not Available';
        const processedRemarks = tabConfig.remarks.map(remarkConfig => {
            let finalValue: string | null = UNAVAILABLE_TEXT;
            try {
                const rawValue = remarkConfig.extractor(state);

                if (rawValue !== null && rawValue !== undefined) {
                    if (typeof rawValue === 'object' && 'value' in rawValue) {
                        finalValue = rawValue.value ? String(rawValue.value) : UNAVAILABLE_TEXT;
                    } else if (rawValue !== '') {
                        finalValue = String(rawValue);
                    }
                }
            } catch (e) {
                console.error(`Error processing chronology remark "${remarkConfig.title}" in tab "${tabConfig.tabName}":`, e);
            }
            console.log(`[DEBUG] Processing chronology remark:`, { title: remarkConfig.title, key: remarkConfig.key, value: finalValue });
            return { title: remarkConfig.title, key: remarkConfig.key, value: finalValue };
        });
        return { remarks: processedRemarks };
    }


    public static formatInvolvedParties(persons: any[]): string | null {
        console.log('[DEBUG] formatInvolvedParties - Received input:', persons);
        if (!persons || !Array.isArray(persons) || persons.length === 0) return null;
        
        return persons.map(person => {
            const name = person.name || 'N/A';
            const action = person.action || 'No action';
            const approver = person.approver || 'N/A';
            const date = person.date || 'N/A';
            
            return `${name} (Action: ${action}, Approver: ${approver}, Date: ${date})`;
        }).join('; ');
    }


    public static formatPolicyImprovements(policies: any[]): string | null {
        console.log('[DEBUG] formatPolicyImprovements - Received input:', policies);

        if (!policies || !Array.isArray(policies) || policies.length === 0) {
            console.log('[DEBUG] formatPolicyImprovements - Input is invalid or empty, returning null.');
            return null
        };
        return policies.map(p => p.policy_change).filter(Boolean).join('; ');
    }

}



const ALL_TABS_CONFIG: TabConfig[] = [
    {
        tabName: 'Investigation',
        layout: 'key-value',
        fields: [
            {
                field: "Case UIN",
                key: "case-uin",
                extractor: (state) => state.caseInfoData?.data?.uin,
            },
            {
                field: "Account Number(s)",
                key: "loan-reference",
                extractor: (state) => state.caseInfoData?.data?.loan_reference_ids,
                formatter: ReportDataService.flattenToCommaString,
            },
            {
                field: "Region/Zone/Branch",
                key: "region-zone-branch",
                extractor: (state) => ({
                    region: state.caseInfoData?.data?.region,
                    zone: state.caseInfoData?.data?.zone,
                    branch: state.caseInfoData?.data?.branch,
                }),
                formatter: (values) => ReportDataService.concatenateFields(values)
            },
            {
                field: "Nature of Suspicion",
                key: "investigation-nature",
                extractor: (state) => state.caseInfoData?.data?.investigation_nature,
            },
            {
                field: "Total Amount Involved",
                key: "total-amount-involved",
                extractor: (state) => "-",
            },
            {
                field: "Referred By",
                key: "referred-by",
                extractor: (state) => state.caseInfoData?.data?.referrer,
            },
            {
                field: "Primary Party/Parties",
                key: "primary-party-involved",
                extractor: (state) => "-",
            },
            {
                field: "Employee Involvement",
                key: "employee-involvement",
                extractor: (state) => state.caseInfoData?.data?.employee_involvement,
                formatter: (value) => (value === null || value === undefined ? null : value ? "Yes" : "No"),
            },
            {
                field: "Employee Code(s)",
                key: "employee-code",
                extractor: (state) => "-",
            },
            {
                field: "Referral Date",
                key: "date-referred-fcu",
                extractor: (state) => state.caseInfoData?.data?.referral_date,
                formatter: ReportDataService.formatDate
            },
            {
                field: "Brief Background",
                key: "brief-background",
                extractor: (state) => state.caseInfoData?.data?.background,
            },
            {
                field: "Source",
                key: "source",
                extractor: (state) => state.caseInfoData?.data?.source,
            },
            {
                field: "Modus Operandi",
                key: "modus-operandi",
                extractor: (state) => state.caseObservationsData?.observation?.modus_operandi?.value,
            },
            {
                field: "Investigating Officer",
                key: "primary-investigator",
                extractor: (state) => state.caseInfoData?.data?.investigator_email,
            },
            {
                field: "Product",
                key: "product",
                extractor: (state) => state.caseInfoData?.data?.product,
            },
        ]
    },
    {
        tabName: 'Financial Impact',
        layout: 'multi-column',
        recordsExtractor: (state) => state.caseOverviewData?.records || [],
        rows: [
            { title: 'Exposure', key: 'exposure', formatter: ReportDataService.formatCurrency },
            { title: 'DPD', key: 'dpd' },
            { title: 'Current Exposure - Principal', key: 'current_exposure_principal' },
            { title: 'Current Exposure - Interest', key: 'current_exposure_interest' },
            { title: 'Current Exposure - Other Charges', key: 'current_exposure_other_charges' },
            { title: 'Sanctioned Amount', key: 'sanctioned_amount' },
            { title: 'Disbursal Amount', key: 'disbursed_amount', formatter: ReportDataService.formatCurrency },
            { title: 'Disbursal Date', key: 'disbursement_date' },
            { title: 'Provisioned Amount', key: 'provisioned_amount', formatter: ReportDataService.formatCurrency },
            { title: 'Written off Amount', key: 'written_off_amount', formatter: ReportDataService.formatCurrency },
            { title: 'Insured Amount', key: 'insured_amount' },
        ]
    },
    {
        tabName: 'Employee Involvement',
        layout: 'key-value',
        fields: [
            { field: 'Employee Name', key: 'employee_name', extractor: () => null },
            { field: 'Employee Code', key: 'employee_code', extractor: () => null },
            { field: 'Grade', key: 'employee_grade', extractor: () => null },
            { field: 'Designation', key: 'employee_designation', extractor: () => null },
            { field: 'Charges/Allegations', key: 'charges_allegations', extractor: () => null },
            { field: 'Charges/Allegations Substantiated?', key: 'charges_substantiated', extractor: () => null },
            { field: 'DAP Clause', key: 'dap_clause', extractor: () => null },
            { field: 'Recommendation Action as per DAP', key: 'action_recommended', extractor: () => null },
            { field: 'Whether Suspended?', key: 'suspended', extractor: () => null },
            { field: 'Suspension Date', key: 'suspension_date', extractor: () => null },
            { field: 'Whether Exited?', key: 'exited', extractor: () => null },
        ]
    },
    {
        tabName: 'Recommendations',
        layout: 'recommendations' as any,
        recordsExtractor: (state) => {
            const partiesWithActions = Array.isArray(state.partiesWithRecommendedAction)
                ? state.partiesWithRecommendedAction
                : [];

            // Keep the legacy "persons_involved" table shape for minimal UI change,
            // but source data from Parties Involved (recommended actions).
            const personsFromPartiesInvolved = partiesWithActions.map((party: any) => ({
                name: party?.name ?? null,
                action: Array.isArray(party?.recommended_actions)
                    ? party.recommended_actions.filter(Boolean).join(", ")
                    : null,
                approver: party?.recommended_action_approver ?? null,
                date: party?.recommended_action_implementation_date ?? null,
            }));

            const legacyPersonsFromRecommendations = (state.caseRecommendationsData?.persons_involved || []).map((p: any) => ({
                name: p?.name ?? null,
                action: p?.action ?? null,
                approver: p?.approver ?? null,
                date: p?.date ?? null,
            }));

            return {
                persons_involved: personsFromPartiesInvolved.length > 0
                    ? personsFromPartiesInvolved
                    : legacyPersonsFromRecommendations,
                policy_improvements: state.isPolicyImprovementV2Enabled
                    ? (state.policyImprovementsV2 || []).map((item: any) => ({
                        ...item,
                        date: item.implementation_date,
                      }))
                    : state.caseRecommendationsData?.policy_improvements || []
            };
        },
        rows: [
            {
                title: "Actions Against Involved Parties",
                rows: [
                    { title: 'Party Involved', key: 'name' },
                    { title: 'Recommended Action', key: 'action' },
                    { title: 'Approved By', key: 'approver' },
                    { title: 'Implementation Date', key: 'date', formatter: ReportDataService.formatDate },
                ],
                transpose: true
            },
            {
                title: "Process & Policy Improvements",
                rows: [
                    { title: 'Policy Changes', key: 'policy_change' },
                    { title: 'Approved By', key: 'approver' },
                    { title: 'Implementation Date', key: 'date', formatter: ReportDataService.formatDate },
                ],
                transpose: true
            }
        ] as any,
        transpose: true
    },
    {
        tabName: 'Evidences',
        layout: 'evidence' as const,
    },
    {
        tabName: 'Chronology',
        layout: 'chronology',
        remarks: [
            {
                title: 'Case Overview from LOS & LMS',
                key: 'los-lms-remarks',
                extractor: (state) => state.remarks?.['los-lms-remarks'],
            },
            {
                title: 'Customer Document & Profile Verifications',
                key: 'borrower-verification-remarks',
                extractor: (state) => state.remarks?.['borrower-verification-remarks'],
            },
            {
                title: 'Portfolio Analysis',
                key: 'portfolio-analysis-remarks',
                extractor: (state) => state.remarks?.['portfolio-analysis-remarks'],
            },
            {
                title: 'Interview Findings',
                key: 'interview-findings-remarks',
                extractor: (state) => state.remarks?.['interview-findings-remarks'],
            },
            {
                title: 'Legal and Technical Verification',
                key: 'collateral-verification-remarks',
                extractor: (state) => state.remarks?.['collateral-verification-remarks'],
            },
        ]
    },
    
];